if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      displayedMediaIds = new Set();
      responseCache = new Map();
      prefetchCache = new Set();
      prefetchTimeout = null;

      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');
        this.currentRequestUrl = null;
        this.isRequestInFlight = false;
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        const currentMediaItems = this.querySelectorAll('[data-media-id]');
        currentMediaItems.forEach((item) => {
          const mediaId = item.dataset.mediaId;
          if (mediaId) this.displayedMediaIds.add(mediaId);
        });

        const variantData = this.querySelector('[data-selected-variant]');
        if (variantData) {
          try {
            const currentVariant = JSON.parse(variantData.textContent);
            if (currentVariant?.id) this.injectMetafieldImages(currentVariant.id);
          } catch (e) {
            console.error('ProductInfo: Error parsing selected variant on load', e);
          }
        }

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );
        this.addEventListener('skio::update-selling-plan', (event) => {
          this.handleSkioUpdateSellingPlan(event);
        });

        this.initQuantityHandlers();
        this.initializeImagePrefetch();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;
        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;
        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.cleanupImagePrefetch();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          if (newNode.querySelector('[data-shopify="payment-button"]') && window?.Shopify?.PaymentButton) {
            window.Shopify.PaymentButton.init();
          }
          if (newNode.querySelector('product-model, [id^="ProductJSON-"]') && window?.ProductModel) {
            window.ProductModel.loadShopifyXR();
          }
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;
        this.resetProductFormState();
        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      handleSkioUpdateSellingPlan(event) {
        if (event.detail.sellingPlan?.id) {
          this.sellingPlan = event.detail.sellingPlan.id;
        } else {
          this.sellingPlan = null;
        }
        const selectedOptionValues = this.variantSelectors?.selectedOptionValues || [];
        const productUrl = this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: null,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);
          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
              0
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
              0
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        if (this.isRequestInFlight && this.currentRequestUrl === requestUrl) return;
        this.currentRequestUrl = requestUrl;

        if (this.responseCache.has(requestUrl)) {
          const cachedResponse = this.responseCache.get(requestUrl);
          this.pendingRequestUrl = null;
          const html = new DOMParser().parseFromString(cachedResponse, 'text/html');
          callback(html);
          if (targetId) document.querySelector(`#${targetId}`)?.focus();
          this.isRequestInFlight = false;
          return;
        }

        this.isRequestInFlight = true;
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            this.responseCache.set(requestUrl, responseText);
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            if (targetId) document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name !== 'AbortError') console.error(error);
          })
          .finally(() => {
            if (this.currentRequestUrl === requestUrl) this.isRequestInFlight = false;
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];
        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);
        if (optionValues.length) params.push(`option_values=${optionValues.join(',')}`);
        if (this.sellingPlan) params.push(`selling_plan=${this.sellingPlan}`);
        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks, [], 0);
        }
      }

      updateOption1Availability(selectedOptions) {
        const variantsScript = this.querySelector('[data-product-variants]');
        const optionsScript = this.querySelector('[data-product-options]');
        if (variantsScript) this.variants = JSON.parse(variantsScript.textContent);
        if (optionsScript) this.productOptions = JSON.parse(optionsScript.textContent);
        if (
          !this.variants ||
          !this.productOptions ||
          this.productOptions.length == 1 ||
          this.productOptions[0]?.name === 'Flavor'
        ) return;

        const options1 = this.productOptions[0].values;
        options1.forEach((option1Value) => {
          const option1El = this.querySelector(`input[value="${option1Value}"]`);
          if (!option1El) return;
          const variantExist = this.variants.some((variant) => {
            if (variant.options[0] !== option1Value) return false;
            for (let i = 1; i <= selectedOptions.length; i++) {
              const variantOptionValue = variant.options[i];
              const selectedOptionValue = selectedOptions[i];
              if (selectedOptionValue !== undefined && variantOptionValue !== selectedOptionValue) return false;
            }
            return true;
          });
          option1El.disabled = !variantExist;
        });
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          const sectionId = this.sectionId;
          const section = this.dataset.section;

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateOption1Availability(variant?.options);

          if (!variant) { this.setUnavailable(); return; }

          this.updateMedia(html, variant?.featured_media?.id);
          if (variant?.id) this.injectMetafieldImages(variant.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${sectionId}`);
            const destination = this.querySelector(`#${id}-${section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('FreeGift');

          const quantityRulesEl = this.querySelector(`#Quantity-Rules-${section}`);
          const volumeNoteEl = this.querySelector(`#Volume-Note-${section}`);

          requestAnimationFrame(() => {
            const flavorDrawerSource = html.querySelector(`#FlavorDrawer-${sectionId}`);
            if (flavorDrawerSource) {
              const flavorDrawerDestination = document.querySelector(`#FlavorDrawer-${section}`);
              if (flavorDrawerDestination) {
                flavorDrawerDestination.innerHTML = flavorDrawerSource.innerHTML;
                flavorDrawerDestination.dispatchEvent(new CustomEvent('sb-flavor-drawer:refreshed', { bubbles: true }));
              }
            }
            this.updateQuantityRules(sectionId, html);
            quantityRulesEl?.classList.remove('hidden');
            volumeNoteEl?.classList.remove('hidden');
          });

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          try {
            const bisTrigger = this.querySelector(`#KlaviyoBisTrigger-${sectionId}`);
            const updatedSubmitButton = html.getElementById(`ProductSubmitButton-${sectionId}`);
            const isOos = updatedSubmitButton?.hasAttribute('disabled') ?? true;
            const currentSubmitButton = this.querySelector(`#ProductSubmitButton-${sectionId}`);
            if (currentSubmitButton) {
              currentSubmitButton.toggleAttribute('hidden', isOos);
              currentSubmitButton.style.display = isOos ? 'none' : '';
            }
            if (bisTrigger) {
              bisTrigger.toggleAttribute('hidden', !isOos);
              bisTrigger.style.display = isOos ? '' : 'none';
            }
          } catch (e) {
            console.error('ProductInfo: ATC/BIS toggle failed', e);
          }

          this.productForm?.updateSubmitButtonPriceLabel?.(variant);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: { sectionId, html, variant },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        if (document.querySelector(`div[data-variant-sfp="${variantId}"]`)) {
          document.querySelectorAll('.variant_meta_image').forEach((elem) => {
            if (elem.dataset.variantSfp == variantId) {
              elem.classList.remove('hidden');
            } else {
              elem.classList.add('hidden');
            }
          });
          document.getElementById('main-product-sfp')?.classList.add('hidden');
        } else {
          document.querySelectorAll('div[data-variant-sfp]').forEach((elem) => elem.classList.add('hidden'));
          document.getElementById('main-product-sfp')?.classList.remove('hidden');
        }
      }

      updateURL_OLD(url, variantId) {
        const components = [];
        if (variantId) components.push(`variant=${variantId}`);
        if (this.sellingPlan) components.push(`selling_plan=${this.sellingPlan}`);
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${components.length ? `?${components.join('&')}` : ''}`
        );
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${components.length ? `?${components.join('&')}` : ''}`);
      }

      updateURL(url, variantId) {
        console.log("updated variant id and selling plan in url");
        // current full URL lo (taaki existing params mile)
        const currentUrl = new URL(window.location.href);

        // base path update karo (product URL change ho raha ho to)
        currentUrl.pathname = url;

        // variant set karo
        if (variantId) {
          currentUrl.searchParams.set('variant', variantId);
        }

        // selling plan set karo
        if (this.sellingPlan) {
          currentUrl.searchParams.set('selling_plan', this.sellingPlan);
        }

        // share button ke liye bhi updated URL bhejo
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${currentUrl.pathname}${currentUrl.search}`
        );

        // agar URL update disable hai to return
        if (this.dataset.updateUrl === 'false') return;

        // final URL replace karo (UTM + variant dono rahenge)
        window.history.replaceState({}, '', currentUrl.toString());
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);
        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      /* ─────────────────────────────────────────────────────────────────────
       * METAFIELD IMAGE INJECTION
       * ───────────────────────────────────────────────────────────────────── */

      /**
       * Read variant metafield image JSON embedded by the gallery snippet.
       */
      getVariantMetafieldImages(variantId) {
        const metafieldScript = this.querySelector('[data-variant-metafield-images]');
        if (!metafieldScript) return [];
        try {
          const metafieldData = JSON.parse(metafieldScript.textContent);
          return metafieldData[variantId]?.volume_count_images || [];
        } catch (e) {
          console.error('ProductInfo: Error parsing metafield images', e);
          return [];
        }
      }

      /**
       * Build the main gallery <li> for a metafield image.
       *
       * countIndex (1-based):
       *   array[0] → countIndex 1  (Count 2 image)
       *   array[1] → countIndex 2  (Count 3 image)
       *
       * Starts hidden; buybox showCountImage() reveals the correct one.
       */
      buildMetafieldMediaItem(imgData, sectionId, countIndex) {
        const li        = document.createElement('li');
        const uniqueId  = `metafield-${imgData.id || Date.now()}-${countIndex}`;

        li.id                     = `Slide-${sectionId}-${uniqueId}`;
        li.className              = 'product__media-item grid__item slider__slide scroll-trigger hidden';
        li.dataset.mediaId        = `${sectionId}-${uniqueId}`;
        li.dataset.metafieldImage = 'true';
        li.dataset.countIndex     = countIndex;

        li.innerHTML = `
          <div class="product-media-container media-type-image media-fit-contain global-media-settings gradient"
               style="--ratio: 1.0; --preview-ratio: 1.0;">
            <modal-opener
              class="product__modal-opener product__modal-opener--image"
              data-modal="#ProductModal-${sectionId}"
            >
              <span class="product__media-icon motion-reduce quick-add-hidden product__media-icon--lightbox"
                    aria-hidden="true">
                <span class="svg-wrapper">
                  <svg role="presentation" fill="none" stroke-width="2" focusable="false"
                       width="15" height="15" class="icon icon-image-zoom" viewBox="0 0 15 15">
                    <circle cx="7.067" cy="7.067" r="6.067" stroke="currentColor"></circle>
                    <path d="M11.4 11.4 14 14" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path>
                    <path d="M7 4v6M4 7h6" stroke="currentColor"></path>
                  </svg>
                </span>
              </span>
              <div class="loading__spinner hidden">
                <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66">
                  <circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle>
                </svg>
              </div>
              <div class="product__media media media--transparent">
                <img
                  src="${imgData.src}"
                  srcset="${imgData.srcset}"
                  alt="${imgData.alt}"
                  width="${imgData.width}"
                  height="${imgData.height}"
                  loading="lazy"
                  class="image-magnify-lightbox"
                  sizes="(min-width: 1300px) 600px, (min-width: 990px) calc(50.0vw - 10rem), (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw / 1 - 4rem)"
                >
              </div>
              <button
                class="product__media-toggle quick-add-hidden product__media-zoom-lightbox"
                type="button"
                aria-haspopup="dialog"
                data-media-id="${uniqueId}"
              >
                <span class="visually-hidden">Open media in modal</span>
              </button>
            </modal-opener>
          </div>
        `;

        return li;
      }

      /**
       * Build the thumbnail slider <li> that pairs with a metafield gallery item.
       *
       * The data-target value MUST match the gallery li's data-media-id exactly so
       * Dawn's media-gallery.js can correlate them when setActiveMedia() is called.
       *
       * Starts hidden; revealed in sync with the matching gallery item.
       *
       * @param {Object} imgData
       * @param {string} sectionId
       * @param {number} countIndex   1-based, mirrors gallery item's data-count-index
       * @param {string} mediaId      Full mediaId: "{sectionId}-metafield-…"
       * @param {number} position     1-based position number for aria-label
       */
      buildMetafieldThumbnailItem(imgData, sectionId, countIndex, mediaId, position) {
        const li      = document.createElement('li');
        const thumbId = `Thumbnail-${sectionId}-metafield-${countIndex}`;

        li.id                     = `Slide-Thumbnails-${sectionId}-metafield-${countIndex}`;
        li.className              = 'thumbnail-list__item slider__slide hidden';
        li.dataset.target         = mediaId;   // must match gallery li data-media-id
        li.dataset.mediaPosition  = position;
        li.dataset.metafieldImage = 'true';
        li.dataset.countIndex     = countIndex;

        li.innerHTML = `
          <button
            class="thumbnail global-media-settings global-media-settings--no-shadow"
            aria-label="Load image ${position}"
            aria-controls="GalleryViewer-${sectionId}"
            aria-describedby="${thumbId}"
          >
            <img
              src="${imgData.src}"
              srcset="${imgData.srcset}"
              alt="${imgData.alt}"
              loading="lazy"
              id="${thumbId}"
              sizes="(min-width: 1300px) calc(((1300px - 100px) * 0.65 - 4rem) / 4),
                     (min-width: 990px) calc((65vw - 4rem) / 4),
                     (min-width: 750px) calc((100vw - 15rem) / 8),
                     calc((100vw - 8rem) / 3)"
            >
          </button>
        `;

        return li;
      }

      /**
       * Remove all previously injected metafield items (gallery + thumbnail sliders)
       * and inject fresh ones for the given variant.
       *
       * After injection fires 'product-info:metafield-images-injected' so the buybox
       * and modal can restore the correct active image.
       */
      injectMetafieldImages(variantId) {
        // ── 1. Remove stale metafield injections from gallery AND thumbnail lists ──
        this.querySelectorAll('[data-metafield-image="true"]').forEach((el) => el.remove());

        // ── 2. Get fresh images for this variant ──────────────────────────────────
        const metafieldImages = this.getVariantMetafieldImages(variantId);
        if (!metafieldImages.length) return;

        const sectionId = this.dataset.section;

        // ── 3. Inject into every media-gallery within this product-info ───────────
        this.querySelectorAll('media-gallery').forEach((gallery) => {
          const galleryUl   = gallery.querySelector('ul[id^="Slider-Gallery-"]');
          const thumbnailUl = gallery.querySelector('ul[id^="Slider-Thumbnails-"]');

          metafieldImages.forEach((imgData, arrayIndex) => {
            /*
             * countIndex is 1-based and shared between the gallery li and its
             * paired thumbnail li so the buybox can address both with the same index:
             *   array[0] → countIndex 1  →  Count 2 image
             *   array[1] → countIndex 2  →  Count 3 image
             */
            const countIndex = arrayIndex + 1;

            // Build gallery item — prepend so metafield images appear first
            if (galleryUl) {
              galleryUl.prepend(
                this.buildMetafieldMediaItem(imgData, sectionId, countIndex)
              );
            }

            // Build thumbnail item — prepend to match gallery order
            if (thumbnailUl) {
              const uniqueId = `metafield-${imgData.id || 'img'}-${countIndex}`;
              const mediaId  = `${sectionId}-${uniqueId}`;
              const position = countIndex; // 1-based from front (prepended)

              thumbnailUl.prepend(
                this.buildMetafieldThumbnailItem(imgData, sectionId, countIndex, mediaId, position)
              );
            }
          });
        });

        // ── 4. Notify subscribers ─────────────────────────────────────────────────
        this.dispatchEvent(new CustomEvent('product-info:metafield-images-injected', {
          bubbles: true,
          detail: { variantId }
        }));
      }

      /* ─────────────────────────────────────────────────────────────────────
       * MEDIA UPDATE
       * ───────────────────────────────────────────────────────────────────── */

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaKey = `${this.dataset.section}-${variantFeaturedMediaId}`;
        const mediaGalleriesSource = this.querySelectorAll('media-gallery ul');
        const mediaGalleriesDestination = html.querySelectorAll('media-gallery ul');

        if (mediaGalleriesSource.length !== mediaGalleriesDestination.length) return;

        const hasZoomOnHover = this.hasAttribute('data-zoom-on-hover');

        mediaGalleriesSource.forEach((mediaGallerySource, index) => {
          const mediaGalleryDestination = mediaGalleriesDestination[index];

          const refreshSourceData = () => {
            const mediaGallerySourceItems = Array.from(
              mediaGallerySource.querySelectorAll(
                'li[data-media-id]:not([data-metafield-image]), li[data-target]:not([data-metafield-image])'
              )
            );
            const sourceSet = new Set(
              mediaGallerySourceItems.map((item) => item.dataset.mediaId || item.dataset.target)
            );
            const sourceMap = new Map(
              mediaGallerySourceItems.map((item, index) => [
                item.dataset.mediaId || item.dataset.target,
                { item, index },
              ])
            );
            return [mediaGallerySourceItems, sourceSet, sourceMap];
          };

          if (mediaGallerySource && mediaGalleryDestination) {
            let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

            const mediaGalleryDestinationItems = Array.from(
              mediaGalleryDestination.querySelectorAll('li[data-media-id], li[data-target]')
            );
            const destinationSet = new Set(
              mediaGalleryDestinationItems.map((item) => item.dataset.mediaId || item.dataset.target)
            );
            let shouldRefresh = false;

            for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
              const key = mediaGalleryDestinationItems[i].dataset.mediaId || mediaGalleryDestinationItems[i].dataset.target;
              if (!sourceSet.has(key)) {
                mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
                shouldRefresh = true;
              }
            }

            for (let i = 0; i < mediaGallerySourceItems.length; i++) {
              const key = mediaGallerySourceItems[i].dataset.mediaId || mediaGallerySourceItems[i].dataset.target;
              if (!destinationSet.has(key)) {
                mediaGallerySourceItems[i].remove();
                shouldRefresh = true;
              }
            }

            if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

            const nthOfTypeCache = new Map();
            const getNthOfType = (n) => {
              if (!nthOfTypeCache.has(n)) {
                nthOfTypeCache.set(n, mediaGallerySource.querySelector(`li:nth-of-type(${n})`));
              }
              return nthOfTypeCache.get(n);
            };

            mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
              const key = destinationItem.dataset.mediaId || destinationItem.dataset.target;
              const sourceData = sourceMap.get(key);
              if (sourceData && sourceData.index !== destinationIndex) {
                mediaGallerySource.insertBefore(sourceData.item, getNthOfType(destinationIndex + 1));
                [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
                nthOfTypeCache.clear();
              }
            });
          }
        });

        const mediaGalleries = this.querySelectorAll(`media-gallery`);
        mediaGalleries.forEach((gallery) =>
          gallery.setActiveMedia?.(`${this.dataset.section}-${variantFeaturedMediaId}`, true)
        );

        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;

        if (hasZoomOnHover) {
          requestAnimationFrame(() => { enableZoomOnHover(2); });
        }

        this.displayedMediaIds.add(mediaKey);
      }

      /* ─────────────────────────────────────────────────────────────────────
       * QUANTITY
       * ───────────────────────────────────────────────────────────────────── */

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;
        if (max) { this.quantityInput.max = max; } else { this.quantityInput.removeAttribute('max'); }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;
        this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();
        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) { current.setAttribute(attribute, valueUpdated); }
              else { current.removeAttribute(attribute); }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      /* ─────────────────────────────────────────────────────────────────────
       * GETTERS
       * ───────────────────────────────────────────────────────────────────── */

      get productForm()         { return this.querySelector(`product-form`); }
      get productModal()        { return document.querySelector(`#ProductModal-${this.dataset.section}`); }
      get pickupAvailability()  { return this.querySelector(`pickup-availability`); }
      get variantSelectors()    { return this.querySelector('variant-selects'); }
      get sectionId()           { return this.dataset.originalSection || this.dataset.section; }

      get relatedProducts() {
        const id = SectionId.getIdForSection(SectionId.parseId(this.sectionId), 'related-products');
        return document.querySelector(`product-recommendations[data-section-id^="${id}"]`);
      }

      get quickOrderList() {
        const id = SectionId.getIdForSection(SectionId.parseId(this.sectionId), 'quick_order_list');
        return document.querySelector(`quick-order-list[data-id^="${id}"]`);
      }

      /* ─────────────────────────────────────────────────────────────────────
       * IMAGE PREFETCH
       * ───────────────────────────────────────────────────────────────────── */

      initializeImagePrefetch() {
        if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return;
        this.addEventListener('mouseenter', this.handleOptionHover.bind(this), true);
        this.addEventListener('mouseleave', this.handleOptionLeave.bind(this), true);
      }

      cleanupImagePrefetch() {
        if (this.prefetchTimeout) { clearTimeout(this.prefetchTimeout); this.prefetchTimeout = null; }
      }

      handleOptionHover(event) {
        const target = event.target.closest('[data-flavor-option], [data-option-value-id]');
        if (!target || target.disabled || !this.contains(target)) return;
        if (this.prefetchTimeout) clearTimeout(this.prefetchTimeout);
        this.prefetchTimeout = setTimeout(() => { this.prefetchFeaturedMedia(target); }, 250);
      }

      handleOptionLeave(event) {
        const target = event.target.closest('[data-flavor-option], [data-option-value-id]');
        if (!target) return;
        if (this.prefetchTimeout) { clearTimeout(this.prefetchTimeout); this.prefetchTimeout = null; }
      }

      prefetchFeaturedMedia(element) {
        const featuredMediaId = element.dataset.featuredMediaId;
        if (!featuredMediaId) return;
        const mediaKey = `${this.dataset.section}-${featuredMediaId}`;
        const mediaElement = this.querySelector(`[data-media-id="${mediaKey}"]`);
        if (!mediaElement) return;
        const img = mediaElement.querySelector('img');
        if (!img) return;
        let imageUrl = img.src;
        if (img.srcset) {
          const largestEntry = img.srcset.split(',').pop()?.trim();
          if (largestEntry) imageUrl = largestEntry.split(' ')[0];
        }
        if (this.prefetchCache.has(imageUrl)) return;
        const link = document.createElement('link');
        link.rel  = 'prefetch';
        link.as   = 'image';
        link.href = imageUrl;
        document.head.appendChild(link);
        this.prefetchCache.add(imageUrl);
      }
    }
  );
}